import {
  BlockLayouts,
  BlockTypes,
  EventCardLayouts,
  ExperienceChildConfigKind,
  ExperienceLayouts,
} from '@/types/gql.generated';
import type { PageExperience } from '../types';

// plannerCalendar → plannerMonth compat.
//
// The interactive planner renders ONLY through the block system (PageLayoutRenderer
// → PlannerBlockLayout), which knows plannerMonth/plannerWeek but not the legacy
// plannerCalendar. So an interactive plannerCalendar must always be remapped to
// plannerMonth — this is independent of printableBlockEnabled, which gates the
// PRINTABLE renderer swap (classic template vs block), not the interactive path.
// Without the remap an interactive plannerCalendar hits PageLayoutRenderer's
// default and errors with "Unsupported layout plannerCalendar".
//
// The layout-id remap (remapPlannerCalendarLayoutId) is scoped to the INTERACTIVE
// kind. The experience/block remap (remapPlannerCalendarExperience) handles both
// kinds: printable plannerCalendar children block-render through the month page
// (loadTemplateRenderer) when printableBlockEnabled is on, and their all-None
// blocks need swapping too or the grid draws blank; with the flag off they keep
// their classic template and stay untouched.

/** Remap a picker/preview layout id (interactive plannerCalendar → plannerMonth). */
export const remapPlannerCalendarLayoutId = (
  layoutId: ExperienceLayouts | null,
  kind: ExperienceChildConfigKind | null
): ExperienceLayouts | null =>
  layoutId === ExperienceLayouts.PlannerCalendar &&
  kind === ExperienceChildConfigKind.Interactive
    ? ExperienceLayouts.PlannerMonth
    : layoutId;

// plannerCalendar's server definition declares None for every block and card,
// so swapping the layoutId alone would leave the block switches
// (Header/Collection/Footer, EventItem) dispatching on None and rendering
// nothing. Stamp plannerMonth's canonical block set — matching its server
// definition and what the follow-up consolidation will persist.
const PLANNER_MONTH_COMPAT_BLOCKS: PageExperience['blocks'] = [
  {
    id: `compat-${BlockTypes.Header}`,
    sequence: 0,
    type: BlockTypes.Header,
    layoutId: BlockLayouts.LeftTitleHeader,
  },
  {
    id: `compat-${BlockTypes.Collection}`,
    sequence: 1,
    type: BlockTypes.Collection,
    layoutId: BlockLayouts.ResponsiveMonth,
  },
  {
    id: `compat-${BlockTypes.Footer}`,
    sequence: 2,
    type: BlockTypes.Footer,
    layoutId: BlockLayouts.PrintableFooter,
  },
];

// plannerCalendar's server layout declares a single `None` card, so the
// customize panel's card-capability lookup (getEventCardCapabilities) finds no
// match for the remapped card and hides every "Show on this view" toggle. Graft
// plannerMonth's full grid card set — the four display types (Card / Line /
// Bullet / Text), mirroring the server's PLANNER_GRID_CARD_OPTIONS — so the
// capabilities resolve for whichever display type the author selects, not just
// the default GridEventCard.
const PLANNER_MONTH_COMPAT_CARD_OPTIONS: PageExperience['layout']['cardOptions'] =
  [
    EventCardLayouts.GridEventCard,
    EventCardLayouts.GridEventLine,
    EventCardLayouts.GridEventBullet,
    EventCardLayouts.GridEventText,
  ].map((layout) => ({
    layout,
    supportsDate: true,
    supportsEmoji: true,
    supportsDescription: true,
    supportsLocation: true,
  }));

/**
 * Remap a committed plannerCalendar experience so its block-composed page
 * dispatches correctly (plannerCalendar declares None for every block).
 *
 * - Interactive: full remap to plannerMonth in place — layoutId, blocks, a None
 *   event card become plannerMonth's defaults, and the grid card options are
 *   grafted so the customize panel resolves capabilities for every display type.
 *   Always applied (the interactive path is block-only regardless of the flag).
 * - Printable: only when printableBlockEnabled is on does loadTemplateRenderer
 *   route it through the block month page, so only then do its None blocks need
 *   plannerMonth's set; the layoutId and the child's own card stay put. With the
 *   flag off it keeps its classic template and stays untouched.
 *
 * Everything else (title, dates, card display flags) stays the child's own.
 * Returns the experience unchanged when it isn't a plannerCalendar.
 */
export const remapPlannerCalendarExperience = (
  experience: PageExperience,
  printableBlockEnabled: boolean
): PageExperience => {
  if (experience.layoutId !== ExperienceLayouts.PlannerCalendar) {
    return experience;
  }

  if (experience.kind === ExperienceChildConfigKind.Interactive) {
    return {
      ...experience,
      layoutId: ExperienceLayouts.PlannerMonth,
      blocks: PLANNER_MONTH_COMPAT_BLOCKS,
      eventCardLayoutId:
        experience.eventCardLayoutId === EventCardLayouts.None
          ? EventCardLayouts.GridEventCard
          : experience.eventCardLayoutId,
      layout: {
        ...experience.layout,
        cardOptions: PLANNER_MONTH_COMPAT_CARD_OPTIONS,
      },
    };
  }

  if (
    printableBlockEnabled &&
    experience.kind === ExperienceChildConfigKind.Printable
  ) {
    return { ...experience, blocks: PLANNER_MONTH_COMPAT_BLOCKS };
  }

  return experience;
};
